import { RESOLUTION_TYPE } from "../constants";
import type { Claim, ResolutionSummary } from "../types";
import {
  getEffectiveResolution,
  isAwaitingReview,
  isClaimComplete,
} from "./claimState";

const getPendingResolutionSummary = (
  claim: Claim,
  phase: string,
): ResolutionSummary => ({
  type: "pending",
  phase,
  preferredResolution: claim.preferred_resolution,
});

export const getResolutionSummary = (claim: Claim): ResolutionSummary => {
  if (isAwaitingReview(claim)) {
    return getPendingResolutionSummary(claim, "review");
  }

  const resolutionType = getEffectiveResolution(claim);
  const currency = claim.order?.currency || "USD";
  const pendingPhase = isClaimComplete(claim) ? "complete" : "resolution";

  if (resolutionType === RESOLUTION_TYPE.REFUND) {
    const refund = claim.resolution?.refunds?.[0];
    if (!refund) {
      return getPendingResolutionSummary(claim, pendingPhase);
    }

    return {
      type: RESOLUTION_TYPE.REFUND,
      amount: refund.amount,
      currency: refund.currency || currency,
      issuedAt: refund.refund_date || refund.created_at,
    };
  }

  if (resolutionType === RESOLUTION_TYPE.STORE_CREDIT) {
    const credit = claim.resolution?.store_credits?.[0];
    if (!credit) {
      return getPendingResolutionSummary(claim, pendingPhase);
    }

    return {
      type: RESOLUTION_TYPE.STORE_CREDIT,
      amount: credit.amount,
      currency: credit.currency || currency,
      discountCode: credit.discount_code,
      creditType: credit.credit_type,
      issuedAt: credit.created_at,
    };
  }

  if (resolutionType === RESOLUTION_TYPE.RESHIP) {
    const reship = claim.resolution?.reships?.[0];
    if (!reship) {
      return getPendingResolutionSummary(claim, pendingPhase);
    }

    return {
      type: RESOLUTION_TYPE.RESHIP,
      reshipOrderNumber: reship.reship_order_number,
      reshipShipInsureId: reship.reship_ship_insure_id,
      shipment: reship.shipments?.[0],
      createdAt: reship.created_at,
    };
  }

  return getPendingResolutionSummary(claim, pendingPhase);
};
